/* eslint-disable react/forbid-foreign-prop-types */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import FocusLock from 'react-focus-lock';
import * as Styled from './OverlayModal.styled';
import { RemoveScrollContext } from '../../../RemoveScroll/RemoveScroll.context';
import { isBrowser } from '../../../../util/ssr';
import { useQuickView } from '../../../QuickView/QuickView.context';
import OverlayHeader from './OverlayHeader/OverlayHeader';
import ButtonIcon from '../../../Icons/ButtonIcon';
import Icon from '../../../Icons/Icon';
import OverlayFooter from './OverlayFooter/OverlayFooter';
import { BACKDROP_TRANSITION_DURATION } from './OverlayModal.styled';
var ESC_KEYS = ['Esc', 'Escape'];
/**
 * A modal is a controlled component which renders its children outside (and on top of)
 * the normal flow and blocks interaction with the rest of the page.
 * Modals are purposefully interruptive, so they should be used sparingly.
 *
 * Note: Modals come with `24px` of base padding around their content.
 */
var OverlayModal = function (props) {
    var _a;
    var _b = props.backDataLabel, backDataLabel = _b === void 0 ? 'modal_back' : _b, _c = props.canBackdropRequestClose, canBackdropRequestClose = _c === void 0 ? true : _c, children = props.children, _d = props.className, className = _d === void 0 ? '' : _d, _e = props.closeDatalabel, closeDatalabel = _e === void 0 ? 'modal_close' : _e, _f = props.dataLabel, dataLabel = _f === void 0 ? 'modal' : _f, footer = props.footer, footerPrimaryButton = props.footerPrimaryButton, footerSecondaryButton = props.footerSecondaryButton, footerTags = props.footerTags, _g = props.hasHeaderDivider, hasHeaderDivider = _g === void 0 ? true : _g, header = props.header, _h = props.headerAlignment, headerAlignment = _h === void 0 ? 'center' : _h, _j = props.headerVariant, headerVariant = _j === void 0 ? 'utility' : _j, _k = props.height, height = _k === void 0 ? 'auto' : _k, _l = props.isFullscreen, isFullscreen = _l === void 0 ? false : _l, isOpen = props.isOpen, leftNodeHeader = props.leftNodeHeader, _m = props.mediumLayoutTopBottomPadding, mediumLayoutTopBottomPadding = _m === void 0 ? { top: 24, bottom: 24 } : _m, _o = props.modalType, modalType = _o === void 0 ? 'default' : _o, onClickBackButton = props.onClickBackButton, onClickPrimaryButton = props.onClickPrimaryButton, onClickSecondaryButton = props.onClickSecondaryButton, onRemoveTag = props.onRemoveTag, onRequestClose = props.onRequestClose, onSelectSlide = props.onSelectSlide, selectedSlide = props.selectedSlide, _p = props.showHeaderBackButton, showHeaderBackButton = _p === void 0 ? false : _p, _q = props.showHeaderCloseButton, showHeaderCloseButton = _q === void 0 ? true : _q, _r = props.showTitleIcon, showTitleIcon = _r === void 0 ? false : _r, _s = props.size, size = _s === void 0 ? 'regular' : _s, slides = props.slides, _t = props.smallLayoutTopMargin, smallLayoutTopMargin = _t === void 0 ? 12 : _t, _u = props.smallLayoutWithSidePadding, smallLayoutWithSidePadding = _u === void 0 ? true : _u, _v = props.withCollapse, withCollapse = _v === void 0 ? false : _v, _w = props.withPadding, withPadding = _w === void 0 ? true : _w;
    var scrollRef = useRef();
    var isQuickView = useQuickView().isQuickView;
    var isModalClosing = useRef(false);
    var toggleScrollLock = useContext(RemoveScrollContext).toggleScrollLock;
    var _x = useState(false), isScrolled = _x[0], setIsScrolled = _x[1];
    var handleClose = useCallback(function (e) {
        if (isModalClosing.current)
            return;
        if (onRequestClose) {
            onRequestClose(e);
        }
    }, [onRequestClose]);
    var handleBackButton = useCallback(function (e) {
        return onClickBackButton && onClickBackButton(e);
    }, [onClickBackButton]);
    var handleBackdropClose = useCallback(function (e) {
        return canBackdropRequestClose && handleClose(e);
    }, [canBackdropRequestClose, handleClose]);
    var handleSelectSlide = useCallback(function (e) { return onSelectSlide && onSelectSlide(e); }, [onClickBackButton]);
    // Reset the modal closing ref when modal opens
    useEffect(function () {
        if (isOpen) {
            isModalClosing.current = false;
        }
    }, [isOpen]);
    // Esc key handling
    useEffect(function () {
        var handleKeyDown = function (e) {
            if (isOpen && ESC_KEYS.includes(e.key)) {
                handleClose(e);
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return function () {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleClose]);
    // Add browser back button handling
    useEffect(function () {
        var handlePopState = function (e) {
            if (isOpen && !isModalClosing.current) {
                handleClose(e);
            }
        };
        if (isOpen) {
            window.addEventListener('popstate', handlePopState);
        }
        return function () {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, handleClose]);
    useEffect(function () {
        // only add scroll event listener if header is not present and close button is shown
        if (!header && showHeaderCloseButton && scrollRef.current) {
            var handleScroll_1 = function () {
                if (!scrollRef.current)
                    return;
                var scrollPosition = scrollRef.current.scrollTop;
                if (scrollPosition > 0) {
                    setIsScrolled(true);
                }
                else {
                    setIsScrolled(false);
                }
            };
            scrollRef.current.addEventListener('scroll', handleScroll_1);
            return function () {
                var _a;
                (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', handleScroll_1);
            };
        }
    }, [scrollRef.current]);
    return (React.createElement(CSSTransition, { appear: true, in: isOpen, classNames: "modal-".concat(modalType), timeout: BACKDROP_TRANSITION_DURATION, unmountOnExit: true, onEntering: function () {
            toggleScrollLock(true);
        }, onExiting: function () {
            isModalClosing.current = true;
            toggleScrollLock(false);
        }, onExited: function () {
            isModalClosing.current = false;
        } },
        React.createElement(Styled.TransitionContainer, { modalType: modalType },
            React.createElement(FocusLock, { returnFocus: true },
                React.createElement(Styled.Modal, { "data-label": dataLabel, role: "dialog", modalType: modalType, className: className },
                    React.createElement(Styled.Backdrop, { "data-testid": "modal_backdrop", onMouseDown: handleBackdropClose, isFullscreen: isFullscreen }),
                    React.createElement(Styled.Container, { modalType: modalType, "data-testid": "modal_container", isFullscreen: isFullscreen, size: size, height: height, className: className },
                        !header && showHeaderCloseButton ? (React.createElement(Styled.CloseButtonContainer, null,
                            React.createElement(Styled.CloseButton, { isScrolled: isScrolled, isQuickView: isQuickView, onClick: handleClose, "data-label": closeDatalabel, "data-testid": "modal_close", variant: "defaultLight", name: "close" }))) : null,
                        (header || ((_a = slides === null || slides === void 0 ? void 0 : slides.length) !== null && _a !== void 0 ? _a : 0) > 1) && (React.createElement(OverlayHeader, { variant: headerVariant, alignment: headerAlignment, size: size, title: header, withCollapse: withCollapse, titleIcon: showTitleIcon ? (React.createElement(Icon, { size: "mega", name: "arrow-right" })) : null, leftIcon: showHeaderBackButton ? (React.createElement(ButtonIcon, { size: "large", onClick: handleBackButton, "data-label": backDataLabel, "data-testid": "modal_back", variant: "defaultLight", name: "chevron-left" })) : (leftNodeHeader !== null && leftNodeHeader !== void 0 ? leftNodeHeader : null), rightIcon: showHeaderCloseButton ? (React.createElement(ButtonIcon, { onClick: handleClose, "data-label": closeDatalabel, "data-testid": "modal_close", variant: "defaultLight", name: "close" })) : null, slides: slides, onSelectSlide: handleSelectSlide, selectedSlide: selectedSlide || '0', withDivider: hasHeaderDivider })),
                        React.createElement(Styled.Content, { ref: scrollRef, hasHeader: !!header, withCollapse: withCollapse, isFullscreen: isFullscreen, withPadding: withPadding, smallLayoutWithSidePadding: smallLayoutWithSidePadding, smallLayoutTopMargin: smallLayoutTopMargin, mediumLayoutTopBottomPadding: mediumLayoutTopBottomPadding }, children),
                        footer && (React.createElement(OverlayFooter, { primaryButton: footerPrimaryButton, onClickPrimaryButton: onClickPrimaryButton, secondaryButton: footerSecondaryButton, onClickSecondaryButton: onClickSecondaryButton, tags: footerTags, onRemoveTag: onRemoveTag }, footer))))))));
};
var ModalContainer = function (props) {
    var containerRef = useRef(isBrowser() ? document.createElement('div') : null);
    // Setup / teardown
    useEffect(function () {
        var element = containerRef.current;
        if (!element) {
            return;
        }
        document.body.appendChild(element);
        return function () {
            document.body.removeChild(element);
        };
    }, []);
    if (!containerRef.current) {
        return null;
    }
    return createPortal(React.createElement(OverlayModal, __assign({}, props)), containerRef.current);
};
export { OverlayModal as ModalBody };
export default ModalContainer;
